import { useState, useRef, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthHero } from '@/components/auth/AuthHero';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { SettingsModal } from '@/components/auth/SettingsModal';
import { useAuthTransitionStore } from '@/stores/useAuthTransitionStore';

interface LoginData {
  email?: string;
  password: string;
  remember?: boolean;
}

interface SignUpData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password: string;
  passwordConfirm: string;
}

export default function LoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const logoRef = useRef<HTMLImageElement>(null);
  const authPhase = useAuthTransitionStore((s) => s.phase);

  // Login sayfası mount olduğunda store'u resetle (logout sonrası temiz başlangıç)
  useEffect(() => {
    useAuthTransitionStore.getState().reset();
  }, []);

  const handleLogin = async (data: LoginData) => {
    setError('');
    setFieldErrors({});
    const { email, password, remember = true } = data;

    setIsLoading(true);

    // Icon pozisyonunu hemen yakala (submit anında)
    let capturedRect: { x: number; y: number; width: number; height: number } | null = null;
    if (logoRef.current) {
      const r = logoRef.current.getBoundingClientRect();
      capturedRect = { x: r.left, y: r.top, width: r.width, height: r.height };
    }

    router.post(route('login'), { email, password, remember }, {
      onStart: () => {
        // Request sunucuya gitti — animasyonu hemen başlat (optimistik)
        if (capturedRect) {
          useAuthTransitionStore.getState().startTransition(capturedRect);
        }
      },
      onError: (errors) => {
        // Auth başarısız — animasyonu iptal et
        useAuthTransitionStore.getState().reset();
        setIsLoading(false);

        const mapped: Record<string, string> = {};
        if (errors.email) mapped.email = errors.email;
        if (errors.password) mapped.password = errors.password;
        if (errors.error) mapped.email = errors.error;
        setFieldErrors(mapped);

        if (Object.keys(mapped).length === 0) {
          setError('Giriş yapılırken bir hata oluştu');
        }
      },
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  const handleSignUp = async (data: SignUpData) => {
    setError('');
    setFieldErrors({});
    const { firstName, lastName, email, password, passwordConfirm } = data;

    if (email) {
      setIsLoading(true);
      router.post(route('register'), {
        name: `${firstName || ''} ${lastName || ''}`.trim() || 'Kullanıcı',
        email: email,
        password: password,
        password_confirmation: passwordConfirm,
      }, {
        onError: (errors) => {
          setIsLoading(false);
          if (errors.email) setError(errors.email);
          else if (errors.password) setError(errors.password);
          else if (errors.name) setError(errors.name);
          else setError('Kayıt olurken bir hata oluştu');
        },
        onFinish: () => {
          setIsLoading(false);
        },
      });
    }
  };

  return (
    <div className="flex min-h-screen w-full bg-[#fafafa] dark:bg-[#0a0a0a]">
      {/* Left Content */}
      <div className="flex min-h-screen w-full flex-1 items-center justify-center px-6 py-8 sm:py-12 lg:px-8">
        <div className="w-full max-w-[420px]">
          {/* Settings */}
          <div className="absolute right-6 top-6 z-50 lg:right-auto lg:left-6">
            <SettingsModal />
          </div>

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10"
          >
            <div className="flex items-center justify-center gap-2.5 lg:justify-start">
              <img
                ref={logoRef}
                src="/oa-logo-svg1.svg"
                alt="OkurAtölyesi"
                className="h-9 w-9"
                style={{ opacity: authPhase !== 'idle' ? 0 : 1, transition: 'opacity 0.15s' }}
              />
              <span className="text-[22px] font-semibold tracking-tight">
                <span className="text-[#da2992]">okur</span>
                <span className="text-[#1bb8f5]">atölyesi</span>
              </span>
            </div>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8 text-center lg:text-left"
          >
            <h1 className="text-[28px] font-semibold tracking-tight text-[#0a0a0a] dark:text-white">
              {isSignUp ? 'Hesap Oluştur' : 'Giriş Yap'}
            </h1>
            <p className="mt-2 text-[15px] text-[#666] dark:text-[#888]">
              {isSignUp
                ? 'Hemen başla, öğrenmeye devam et.'
                : 'Hesabına giriş yap ve kaldığın yerden devam et.'}
            </p>
          </motion.div>

          {/* Segmented Control */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative mb-8 grid h-11 grid-cols-2 rounded-xl bg-[#f0f0f0] dark:bg-[#1a1a1a] p-1"
          >
            {/* Sliding pill */}
            <motion.div
              className="absolute inset-y-1 left-1 w-[calc(50%_-_4px)] rounded-[10px] bg-white shadow-sm ring-1 ring-[#1bb8f5]/15 dark:bg-[#2a2a2a] dark:ring-[#1bb8f5]/20"
              animate={{ x: isSignUp ? '100%' : 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 35, mass: 0.8 }}
              style={{
                boxShadow: '0 1px 3px 0 rgb(27 184 245 / 0.06), 0 1px 2px -1px rgb(27 184 245 / 0.04)',
              }}
            />
            <button
              type="button"
              onClick={() => { setIsSignUp(false); setError(''); setFieldErrors({}); }}
              className={`relative z-10 flex items-center justify-center rounded-[10px] text-[13px] font-medium transition-colors duration-200 ${
                !isSignUp
                  ? 'text-[#1bb8f5]'
                  : 'text-[#666] dark:text-[#888] hover:text-[#0a0a0a] dark:hover:text-white'
              }`}
            >
              Giriş Yap
            </button>
            <button
              type="button"
              onClick={() => { setIsSignUp(true); setError(''); setFieldErrors({}); }}
              className={`relative z-10 flex items-center justify-center rounded-[10px] text-[13px] font-medium transition-colors duration-200 ${
                isSignUp
                  ? 'text-[#1bb8f5]'
                  : 'text-[#666] dark:text-[#888] hover:text-[#0a0a0a] dark:hover:text-white'
              }`}
            >
              Kayıt Ol
            </button>
          </motion.div>

          {/* Global Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-6"
              >
                <div className="flex items-center gap-2.5 rounded-lg border border-red-200/60 bg-red-50/80 px-3.5 py-3 text-[13px] text-red-600 dark:border-red-800/40 dark:bg-red-900/20 dark:text-red-400">
                  <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <AnimatePresence mode="wait">
              {isSignUp ? (
                <SignUpForm
                  key="signup"
                  isLoading={isLoading}
                  onSubmit={handleSignUp}
                />
              ) : (
                <LoginForm
                  key="login"
                  isLoading={isLoading}
                  onSubmit={handleLogin}
                  fieldErrors={fieldErrors}
                />
              )}
            </AnimatePresence>
          </motion.div>

          {/* Footer */}
          <div className="mt-12">
            <p className="text-center text-[11px] text-[#999] dark:text-[#555]">
              © 2026 Okur Atölyesi. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>

      {/* Right Hero */}
      <div className="hidden h-screen w-[58%] sticky top-0 lg:block lg:p-2.5">
        <div className="relative h-full w-full overflow-hidden rounded-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
          <AuthHero />
        </div>
      </div>
    </div>
  );
}
